<template>
  <div id="actions">
    <card :title="$t('actions:filterActions')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-4">
            <label for="userLogin">{{ $t('actions:userLogin') | capitalize }}</label>
            <input id="userLogin"
                   v-model="userLogin"
                   v-validate="'text'"
                   :data-vv-as="$t('actions:userLogin')"
                   :class="{ 'is-invalid': errors.has('userLogin') }"
                   type="text"
                   name="userLogin"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('userLogin') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="ip">{{ $t('actions:ip') | capitalize }}</label>
            <input id="ip"
                   v-model="ip"
                   v-validate="'ip'"
                   :data-vv-as="$t('actions:ip')"
                   :class="{ 'is-invalid': errors.has('ip') }"
                   type="text"
                   name="ip"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('ip') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="kti">{{ $t('actions:kti') | capitalize }}</label>
            <input id="kti"
                   v-model="kti"
                   v-validate="'text'"
                   :data-vv-as="$t('actions:kti')"
                   :class="{ 'is-invalid': errors.has('kti') }"
                   type="text"
                   name="kti"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('kti') }}</div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="webService">{{ $t('actions:webServiceName') | capitalize }}</label>
            <div class="input-group">
              <multiselect id="webService"
                           v-model="webServiceName"
                           :options="webServices"
                           :show-labels="false"
                           :options-limit="200"
                           :placeholder="$t('actions:searchWebService') | capitalize"
                           track-by="name"
                           label="name"
              >
                <span slot="noResult">{{ $t('actions:noData') | capitalize }}</span>
              </multiselect>
              <span class="input-group-append">
                <button id="resetWebserviceButton" class="btn btn-flat btn-sm" type="button" @click="webServiceName=''"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>

          <div class="form-group col-md-6">
            <label for="period">{{ $t('actions:period') | capitalize }}</label>
            <div class="input-group">
              <flat-pickr id="period"
                          v-model="period"
                          :config="fpConfig"
                          class="form-control"
              />
              <span class="input-group-append">
                <button id="resetPeriodButton" class="btn btn-flat btn-sm" type="button" @click="period=''"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-3">
            <label for="status">{{ $t('actions:status') | capitalize }}</label>
            <input id="status"
                   v-model="status"
                   v-validate="'number|between:100,527'"
                   :data-vv-as="$t('actions:status')"
                   :class="{ 'is-invalid': errors.has('status') }"
                   type="text"
                   name="status"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('status') }}</div>
          </div>

          <div class="form-group col-md-5">
            <label for="userAgent">{{ $t('actions:userAgent') | capitalize }}</label>
            <input id="userAgent"
                   v-model="userAgent"
                   v-validate="'text'"
                   :data-vv-as="$t('actions:userAgent')"
                   :class="{ 'is-invalid': errors.has('userAgent') }"
                   type="text"
                   name="userAgent"
                   class="form-control"
            >
            <div class="invalid-feedback">{{ errors.first('userAgent') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label>{{ $t('actions:duration') | capitalize }}</label>
            <div class="form-inline">
              <label for="durationFrom" class="control-label mr-2">{{ $t('actions:from') | capitalize }}</label>
              <input id="durationFrom"
                     v-model="durationFrom"
                     v-validate="'number|min_value:0|max_value:' + maxFromValue()"
                     :data-vv-as="$t('actions:from')"
                     :class="{ 'is-invalid': errors.has('durationFrom') }"
                     type="text"
                     min="0"
                     max="30000"
                     name="durationFrom"
                     class="form-control w-25"
              >
              <label for="durationTo" class="control-label mx-2">{{ $t('actions:to') }}</label>
              <input id="durationTo"
                     v-model="durationTo"
                     v-validate="'number|min_value:' + minToValue() + '|max_value:30000'"
                     :data-vv-as="$t('actions:to')"
                     :class="{ 'is-invalid': errors.has('durationTo') }"
                     type="text"
                     min="0"
                     max="30000"
                     name="durationTo"
                     class="form-control w-25"
              >
              <label for="durationTo" class="control-label ml-2">ms</label>
              <div class="invalid-feedback">{{ errors.first('durationFrom') }}</div>
              <div class="invalid-feedback">{{ errors.first('durationTo') }}</div>
            </div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('actions:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="actionsGrid"
          :title="$t('actions:listActions')"
          :columns="columns"
          :paginated-dto="actions"
          @load="filter"
    >
      <template slot="date" slot-scope="props">{{ props.dto.date | date }}</template>
      <template slot="parameters" slot-scope="props">
        <a :title="$t('actions:showParameters') | capitalize" href @click.prevent="displayParametersModal(props.dto)">
          {{ props.dto.parameters | stringify | substring(60) }}
        </a>
      </template>
      <template slot="duration" slot-scope="props">{{ props.dto.duration | number('0,0.[00]','',' ms') }}</template>
    </grid>

    <modal id="parametersModal"
           v-model="showParametersModal"
           :title="parametersModalTitle"
           size="lg"
    >
      <!-- Need to use v-html to insert formatted json (secured content) -->
      <!-- eslint-disable-next-line -->
      <pre slot="modal-body" v-html="parametersModalBody" />
    </modal>
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import to from '@/modules/await-to-js'
import formatDate from 'vue-kerlink/dist/filters/date'

export default {
  titleKey: 'actions',

  data () {
    return {
      // Filters
      webServices: [],
      userLogin: '',
      ip: '',
      kti: '',
      webServiceName: '',
      durationFrom: '',
      durationTo: '',
      status: '',
      userAgent: '',
      period: '',
      // Data
      actions: {},
      // Parameters modal
      showParametersModal: false,
      parametersModalTitle: '',
      parametersModalBody: {},

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    columns () {
      return [
        { text: this.$t('actions:date'), slot: 'date' },
        { text: this.$t('actions:userLogin'), formatter: action => action.userLogin },
        { text: this.$t('actions:ip'), formatter: action => action.ip },
        { text: this.$t('actions:kti'), id: 'kti' },
        { text: this.$t('actions:webServiceName'), formatter: action => action.webServiceName },
        { text: this.$t('actions:parameters'), slot: 'parameters' },
        { text: this.$t('actions:status'), formatter: action => action.status, align: 'center' },
        { text: this.$t('actions:userAgent'), formatter: action => action.userAgent },
        { text: this.$t('actions:duration'), slot: 'duration' }
      ]
    }
  },

  mounted () {
    this.getActions()
    this.getWebServices()
  },

  methods: {
    async getWebServices () {
      let error, response
      [error, response] = await to(this.$api.getWebServices(this.$store.getters.application, { fields: 'name', sort: '+name' }))
      if (error || !response) return

      this.webServices = response.data.list
    },

    async getActions (options) {
      const params = this.$utils.getParams({ sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getActions(this.$store.getters.application, params))
      if (error || !response) return

      this.actions = response.data
    },

    displayParametersModal (dto) {
      this.parametersModalTitle = `${dto.webServiceName} - ${dto.status} - ${dto.userLogin} - ` + formatDate(dto.date)
      this.parametersModalBody = this.$utils.highlight(dto.parameters)
      this.showParametersModal = true
    },

    // Make validators for min and max duration filters
    maxFromValue () {
      return this.durationTo && !isNaN(Number(this.durationTo))
        ? Number(this.durationTo)
        : 30000
    },

    minToValue () {
      return this.durationFrom && !isNaN(Number(this.durationFrom))
        ? Number(this.durationFrom)
        : 0
    },

    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      let search = []

      // Add criteria
      if (this.userLogin) search = this.$utils.addSearch(search, 'like', '*' + this.userLogin + '*', 'userLogin')
      if (this.ip) search = this.$utils.addSearch(search, 'eq', this.ip, 'ip')
      if (this.kti) search = this.$utils.addSearch(search, 'eq', this.kti, 'kti')
      if (this.webServiceName) search = this.$utils.addSearch(search, 'eq', this.webServiceName.name, 'webServiceName')
      if (this.durationFrom) search = this.$utils.addSearch(search, 'gte', this.durationFrom, 'duration')
      if (this.durationTo) search = this.$utils.addSearch(search, 'lte', this.durationTo, 'duration')
      if (this.status) search = this.$utils.addSearch(search, 'eq', this.status, 'status')
      if (this.userAgent) search = this.$utils.addSearch(search, 'like', '*' + this.userAgent + '*', 'userAgent')

      // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
      const period = this.period ? this.period.split(' to ').map(v => v * 1000) : []
      if (!isEmpty(period)) {
        search = this.$utils.addSearch(search, 'gte', period[0], 'date')
        search = this.$utils.addSearch(search, 'lte', period[1], 'date')
      }

      // Format criteria
      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      // Get filtered data
      this.getActions(options)
    }
  }
}
</script>
